import { Avatar, Grid, useTheme } from '@material-ui/core';
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import Toolbar from '@material-ui/core/Toolbar';
import Typography from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useRollbarPerson } from '@rollbar/react';
import { Auth } from 'aws-amplify';
import axios from 'axios';
import Hamburger from 'hamburger-react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

// Material

// Components
import { ComptrollerNameContainer, LogoContainer } from './navbar.styles';
import ComptrollerLogo from '../../../../assets/images/comptroller-logo.png';
import SampleAvatar from '../../../../assets/images/sampleAvatar.png';
import { reduxLogout } from '../../../../private/redux/scholarship.actions';
import useUserStatus from '../../../../utils/use-user-status';

const useStyles = makeStyles(
  (
    theme //  Theme
  ) =>
    createStyles({
      root: {
        display: 'flex',
        '& > *': {
          margin: theme.spacing(1)
        }
      },
      avatar: {
        width: theme.spacing(3),
        height: theme.spacing(3),
        fontWeight: 800,
        [theme.breakpoints.only('xs')]: {
          display: 'none'
        }
      },
      large: {
        width: theme.spacing(7),
        height: theme.spacing(7)
      },
      title: {
        lineHeight: '48px',
        fontWeight: 800,
        display: 'inline-block',
        marginRight: '20px',
        verticalAlign: 'middle'
      },
      avatarName: {
        paddingRight: '5px',
        [theme.breakpoints.only('xs')]: {
          display: 'none'
        }
      },
      hideOnMobile: {
        [theme.breakpoints.only('xs')]: {
          display: 'none'
        }
      },
      hideOnPad: {
        [theme.breakpoints.only('xs')]: {
          display: 'none'
        },
        [theme.breakpoints.only('sm')]: {
          display: 'none'
        }
      },
      showOnlyOnMobile: {
        display: 'none',
        [theme.breakpoints.only('xs')]: {
          display: 'inherit'
        }
      },
      cpaBlue: {
        backgroundColor: '#4090CB'
      },
      blackBg: {
        backgroundColor: 'black'
      }
    })
);

const Navbar = ({ dispatchLogout }) => {
  interface UserStatus {
    attributes?: {
      given_name?: string;
      family_name?: string;
      email?: string;
      sub?: string;
    };
  }

  const [comptrollerInfo, setComptrollerInfo] = useState<{
    name: string;
    shortTitle: string;
    longTitle: string;
  } | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [sub, setSub] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const classes = useStyles();
  const navigate = useNavigate();
  const userStatus = useUserStatus() as UserStatus | null;
  const isLoggedIn = null !== userStatus;
  let menu;

  useEffect(() => {
    void axios('https://api.comptroller.texas.gov/cms/comptroller')
      .then((res) => {
        if (typeof res.data === 'object') {
          setComptrollerInfo(res.data);
        }
      })
      .catch(() => {
        setComptrollerInfo(null);
      });
  }, []);

  useEffect(() => {
    if (userStatus?.attributes) {
      const givenName = userStatus.attributes.given_name || '';
      const familyName = userStatus.attributes.family_name || '';
      const displayName = `${givenName} ${familyName}`.trim();

      setFullName(displayName || null);
      setEmail(userStatus.attributes.email || null);
      setSub(userStatus.attributes.sub || null);

      return;
    }

    Auth.currentAuthenticatedUser({
      bypassCache: false
    })
      .then((data) => {
        setFullName(`${data.attributes.given_name} ${data.attributes.family_name}`);
        setEmail(data.attributes.email);
        setSub(data.attributes.sub);
      })
      .catch(() => {
        setFullName(null);
        setEmail(null);
        setSub(null);
      });
  }, [userStatus]);

  useTheme();
  useRollbarPerson({ id: sub, username: fullName, email: email });

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (route?: string | object) => {
    setAnchorEl(null);
    if (route && typeof route === 'string') {
      navigate(route);
    }
  };

  const navButtonStyles = {
    borderRadius: '0',
    height: '48px'
  };

  const handleLogout = async () => {
    await Auth.signOut();
    dispatchLogout();
    navigate('/');
    setAnchorEl(null);
  };

  if (isLoggedIn) {
    menu = (
      <Grid item>
        {/*
          // @ts-ignore - Feather icon element type mismatch with MUI Button endIcon prop.
        */}
        <Button color="inherit" onClick={handleClick} style={navButtonStyles} endIcon={<i data-feather="chevron-down" />}>
          <div className={classes.avatarName}>{fullName}</div>
          <div className={classes.showOnlyOnMobile}>
            <Hamburger toggled={open} label="Show menu" onToggle={handleClick} />
          </div>
          <Avatar src={SampleAvatar} alt="{fullName}" className={classes.avatar} />
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          getContentAnchorEl={null}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button'
          }}
        >
          <MenuItem onClick={() => handleClose('/applications')}>My Applications</MenuItem>
          <MenuItem onClick={() => handleClose('/applications/profiles')}>Family Profiles</MenuItem>
          <MenuItem onClick={() => handleClose('/applications/account')}>Account Settings</MenuItem>
          <MenuItem onClick={() => handleLogout()}>Logout</MenuItem>
        </Menu>
      </Grid>
    );
  } else {
    menu = (
      <Grid item>
        <Button
          color="inherit"
          style={{
            ...navButtonStyles,
            borderLeft: '1px solid rgba(226,226,226,0.3)'
          }}
          component={Link}
          to="/applications"
        >
          <i data-feather="power" />
          Log In
        </Button>
      </Grid>
    );
  }

  return (
    <div>
      <AppBar position="static">
        <Toolbar className={classes.blackBg}>
          <Grid justifyContent="space-between" container>
            <Grid item>
              <LogoContainer>
                <a href={'https://comptroller.texas.gov'}>
                  <img src={ComptrollerLogo} alt="logo" />
                </a>
              </LogoContainer>
            </Grid>
            <Grid item>
              <ComptrollerNameContainer>
                {comptrollerInfo ? (
                  <>
                    <div id="comptroller-name">{comptrollerInfo.name}</div>
                    <div className={classes.showOnlyOnMobile}>
                      {comptrollerInfo.shortTitle}
                      <br />
                      of Public Accounts
                    </div>
                    <div className={classes.hideOnMobile}>{comptrollerInfo.longTitle}</div>
                  </>
                ) : (
                  <>
                    <div className={classes.showOnlyOnMobile}>
                      Texas Comptroller
                      <br />
                      of Public Accounts
                    </div>
                    <div className={classes.hideOnMobile}>Texas Comptroller of Public Accounts</div>
                  </>
                )}
              </ComptrollerNameContainer>
            </Grid>
          </Grid>
        </Toolbar>
        <Toolbar variant="dense" className={classes.cpaBlue}>
          <Grid justifyContent="space-between" container>
            <Grid item>
              <Typography variant="subtitle2" className={classes.title}>
                <Link to={'/'} style={{ textDecoration: 'none', color: '#fff' }}>
                  MTPF: Match the Promise Foundation&#8480;
                </Link>
              </Typography>
              <Button
                className={classes.hideOnPad}
                color="inherit"
                startIcon={<i data-feather="airplay" />}
                style={navButtonStyles}
                component={Link}
                to="/applications"
              >
                My Applications
              </Button>
              <Button
                className={classes.hideOnPad}
                color="inherit"
                startIcon={<i data-feather="airplay" />}
                style={navButtonStyles}
                component={Link}
                to="/applications/profiles"
              >
                Family Profiles
              </Button>
            </Grid>
            {menu}
          </Grid>
        </Toolbar>
      </AppBar>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDispatchToProps = (dispatch: any) => ({
  dispatchLogout: () => dispatch(reduxLogout())
});

export default connect(null, mapDispatchToProps)(Navbar);
